import {
  format,
  formatDistance,
  formatDistanceStrict,
  formatRelative,
  isDate,
  isValid,
  isSameDay,
  isSameYear,
  endOfWeek,
  startOfWeek,
  addDays,
  subDays,
  parseISO,
} from 'date-fns'
import { zhCN, enUS } from 'date-fns/locale'

import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

const INIT_LOCAL = {
  // 目前台湾的还不支持，暂时使用 zh-CN
  // https://github.com/date-fns/date-fns/blob/master/src/locale/zh-TW/index.js#L29
  'zh-TW': zhCN,
  'zh-CN': zhCN,
  en: enUS,
}['zh-TW'] // TODO

/**
 * 获取有效的 Date 实例
 * @param {Date|String} value
 */
function getDateInstance(value) {
  const date = isDate(value) ? value : parseDate(value)
  if (!isValid(date)) {
    console.error(
      `ReferenceError: It is an invalid instance of Date. The argument date is ${date}, the value is ${value}`,
    )
    throw new Error(
      `ReferenceError: It is an invalid instance of Date. The argument date is ${date}, the value is ${value}`,
    )
  }
  return date
}

/**
 * 指定长度填充数字。不足开头以 0 填充
 * @param {Number} value
 * @param {Number} len
 * @example
 *  pad(12, 4) => '0012'
 *  pad(10, 3) => '010'
 */
function pad(value, len) {
  const padZero = new Array(len).fill(0).join('')
  return `${padZero}${Math.abs(value)}`.slice(-len)
}

/**
 * 把不规范的时区格式，比如 '-8', '+8',有可能是后端返回的时区格式
 * 转为：'-08', '+08'，其他格式不变
 * @param {String|Number} zone
 */
function formatZone(zone) {
  // +h:m => +0h:0m
  const matched = /^([+-])(\d{1,2}):(\d{1,2})$/.exec(zone)
  if (matched) {
    const [, flag, hour, min] = matched
    return `${flag}${pad(hour, 2)}:${pad(min, 2)}`
  }

  const intZone = parseInt(zone, 10)
  if (!isNaN(intZone)) {
    const flag = intZone < 0 ? '-' : '+'
    return intZone <= 24
      ? `${flag}${pad(intZone, 2)}` // +h => +0h
      : `${flag}${pad(intZone, 4)}` // +hm => +hhmm
  }

  return zone
}

export function formatDate(date, pattern, options = {}) {
  const dateInstance = getDateInstance(date)
  return format(dateInstance, pattern, options)
}

export function formatLocalDate(date, pattern = 'PP', options = {}) {
  return formatDate(date, pattern, {
    locale: INIT_LOCAL,
    ...options,
  })
}

export function startOfLocalWeek(date, options = {}) {
  return startOfWeek(getDateInstance(date), {
    locale: INIT_LOCAL,
    ...options,
  })
}

export function endOfLocalWeek(date, options = {}) {
  return endOfWeek(getDateInstance(date), {
    locale: INIT_LOCAL,
    ...options,
  })
}

export function formatLocalDistance(date, baseDate = new Date(), options = {}) {
  return formatDistance(getDateInstance(date), getDateInstance(baseDate), {
    locale: INIT_LOCAL,
    ...options,
  })
}

export function formatLocalDistanceStrict(date, baseDate = new Date(), options = {}) {
  return formatDistanceStrict(getDateInstance(date), getDateInstance(baseDate), {
    locale: INIT_LOCAL,
    ...options,
  })
}

/**
 * @param {String|Date} date
 * @param {String|Date} baseDate
 * @param {Object} options
 */
export function formatLocalRelative(
  date,
  baseDate = new Date(),
  options = {
    // 默认情况下 formatRelative other 时输出格式为 "P"
    // 指定 format 可以指定 other 的输出格式
    format: '',
  },
) {
  const { format: otherFormat, ...realOptions } = options
  const res = formatRelative(getDateInstance(date), getDateInstance(baseDate), {
    locale: INIT_LOCAL,
    ...realOptions,
  })
  return otherFormat && /^\d+/.test(res) ? formatDate(date, otherFormat) : res
}

/**
 * 格式化最近（今天，昨天，明天）日期时间
 * 如果给定日期为非最近日期的，将输出指定格式的日期时间
 * @param {String|Date} date
 * @param {Object} options
 *  @example
 *    options = {
 *      longFormat, //指定非最近日期的输出格式
 *      shortFormat // 最近日期的输出格式
 *    }
 */
export function formatRecentlyDateTime(date, { longFormat = 'yyyy-MM-dd HH:mm', shortFormat = 'HH:mm' } = {}) {
  const dateInstance = getDateInstance(date)
  const now = new Date()
  const dateTime = format(dateInstance, shortFormat)
  const prefix = isSameDay(dateInstance, now)
    ? i18n.t('common.date.today')
    : isSameDay(dateInstance, addDays(now, 1))
    ? i18n.t('common.date.tomorrow')
    : isSameDay(dateInstance, subDays(now, 1))
    ? i18n.t('common.date.yesterday')
    : ''
  return prefix ? `${prefix} ${dateTime}` : format(dateInstance, longFormat)
}

/**
 * 获取简化时间格式
 * 当天：10:23
 * 同年：03-25: 10:23
 * 其他：2023-03-23: 10:23
 */
export function formatShortDateTime(date) {
  const now = new Date()
  const dateTime = getDateInstance(date)

  return isSameDay(dateTime, now)
    ? formatLocalDate(dateTime, 'HH:mm')
    : isSameYear(dateTime, now)
    ? formatLocalDate(dateTime, 'MM-dd HH:mm')
    : formatLocalDate(dateTime, 'yyyy-MM-dd HH:mm')
}

/**
 * 兼容不符合 date-fns-tz 的时区格式，根据指定时间获取目标时区的时间
 * @param {String|Date} date
 * @param {String|Number} zone
 *
 *  date-fns-tz 所支持的时区格式为以下几种类型
 *    /([Z+-].*)$/
 *    /^(Z)$/
 *    /^([+-])(\d{2})$/
 *    /^([+-])(\d{2}):?(\d{2})$/
 *    /(UTC|(?:[a-zA-Z]+\/[a-zA-Z_]+(?:\/[a-zA-Z_]+)?))$/
 */
export function getUtcToZonedTime(date, zone) {
  return utcToZonedTime(getDateInstance(date).toISOString(), formatZone(zone))
}

/**
 * 兼容不符合 date-fns-tz 的时区格式，获取对应指定时区 UTC 时间
 * @param {String|Date} date
 * @param {String|Number} zone
 */
export function getZonedTimeToUtc(date, zone) {
  return zonedTimeToUtc(getDateInstance(date), formatZone(zone))
}

/**
 * 兼容部分浏览器不支持 yyyy-MM-dd hh:mm:ss 这种时间格式，比如 Safari
 * @param {String|Number} value
 */

export function parseDate(value) {
  return parseISO(value)
}
